import React, { useState, useEffect } from 'react';
import { Modal, Stack, Group, Button, ActionIcon, Badge, Text, Loader, Box, Divider } from '@mantine/core';
import { openConfirmModal } from '@mantine/modals';
import { showNotification } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import { updateMessageTags, reclassifyMessage } from '@mygpt/app';
import type { QuestionTagGroupOut } from '@mygpt/types/tag';
import { UnansweredTableItem } from '@/components/MessageHistory/FeedbackTable';

interface TagSelectorModalProps {
  opened: boolean;
  onClose: () => void;
  message: UnansweredTableItem | null;
  tagGroups: QuestionTagGroupOut[];
  loadingTagGroups: boolean;
  onSuccess?: () => void;
}

export const TagSelectorModal: React.FC<TagSelectorModalProps> = ({
  opened,
  onClose,
  message,
  tagGroups,
  loadingTagGroups,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [saving, setSaving] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [selectedTagIds, setSelectedTagIds] = useState<Set<string>>(new Set());
  const [invalidTags, setInvalidTags] = useState<Array<{ id: string; name: string }>>([]); // 已失效的标签

  const { id: messageId, session, tags: currentTags } = message || {};
  const { robot_id: robotId } = session || {};

  // 处理标签选择变化
  const handleTagToggle = (tagId: string) => {
    const newSelectedTags = new Set(selectedTagIds);
    if (newSelectedTags.has(tagId)) {
      newSelectedTags.delete(tagId);
    } else {
      // 最多只能选择3个标签
      if (selectedTagIds.size >= 3) {
        showNotification({
          title: t('tag.max_tags_title'),
          message: t('tag.max_tags_message'),
          color: 'yellow',
          duration: 3000,
        });
        return;
      }

      newSelectedTags.add(tagId);
    }
    setSelectedTagIds(newSelectedTags);
  };

  // 保存标签选择
  const handleSave = async () => {
    if (!robotId || !messageId) return;

    // 检查是否有失效标签被选中
    const hasInvalidTags = invalidTags.some((tag) => selectedTagIds.has(tag.id));
    if (hasInvalidTags) {
      showNotification({
        message: t('tag.invalid_tags_message'),
        color: 'red',
      });
      return;
    }

    setSaving(true);
    try {
      await updateMessageTags(robotId, messageId, {
        tag_ids: Array.from(selectedTagIds),
      });

      showNotification({
        message: t('tag.save_success_message'),
        color: 'green',
      });

      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('保存标签失败:', error);
    } finally {
      setSaving(false);
    }
  };

  // 重新生成标签
  const handleRegenerate = () => {
    if (!robotId || !messageId) return;

    openConfirmModal({
      title: t('tag.regenerate_title'),
      centered: true,
      children: <span>{t('tag.regenerate_desc')}</span>,
      labels: { confirm: t('tag.regenerate'), cancel: t('tag.cancel') },
      confirmProps: {
        color: 'violet',
        loading: regenerating,
      },
      onConfirm: async () => {
        setRegenerating(true);
        try {
          await reclassifyMessage(robotId, messageId);

          showNotification({
            title: t('tag.regenerate_success'),
            message: t('tag.regenerate_success_message'),
            color: 'green',
            duration: 3000,
          });

          onSuccess?.();
          onClose();
        } catch (error) {
          console.error('重新生成标签失败:', error);
        } finally {
          setRegenerating(false);
        }
      },
    });
  };

  // 初始化选中的标签和失效标签
  useEffect(() => {
    if (currentTags) {
      const currentTagIds = new Set(currentTags.map((tag) => tag.id));
      setSelectedTagIds(currentTagIds);

      // 检查失效标签
      const allValidTagIds = new Set(tagGroups.flatMap((group) => group.tags.map((tag) => tag.id)));
      const invalidTagsList = currentTags.filter((tag) => !allValidTagIds.has(tag.id));
      setInvalidTags(invalidTagsList);
    }
  }, [currentTags, tagGroups]);

  return (
    <Modal opened={opened} onClose={onClose} title={t('tag.selector_title')} centered size="lg" radius="md">
      <Stack gap="lg">
        {loadingTagGroups ? (
          <Box style={{ textAlign: 'center', padding: '2rem' }}>
            <Loader size="md" />
            <Text size="sm" c="dimmed" mt="md">
              {t('tag.loading_tags')}
            </Text>
          </Box>
        ) : (
          <Stack gap="md">
            {tagGroups.length === 0 ? (
              <Text c="dimmed" ta="center" py="xl">
                {t('tag.no_tags_available')}
              </Text>
            ) : (
              tagGroups.map((group) => (
                <Box key={group.id}>
                  <Group gap="xs" mb="sm">
                    <Badge variant="filled" size="md">
                      {group.name}
                    </Badge>
                    <Text size="sm" c="dimmed">
                      ({group.tags.length} {t('tag.tags_count')})
                    </Text>
                  </Group>
                  <Group gap="xs">
                    {group.tags.map((tag) => {
                      const isSelected = selectedTagIds.has(tag.id);
                      return (
                        <Button
                          key={tag.id}
                          variant={isSelected ? 'filled' : 'light'}
                          size="xs"
                          onClick={() => handleTagToggle(tag.id)}
                          rightSection={
                            isSelected ? (
                              <ActionIcon
                                size="xs"
                                variant="transparent"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleTagToggle(tag.id);
                                }}
                                className="text-white hover:bg-white/20"
                              >
                                ×
                              </ActionIcon>
                            ) : null
                          }
                          className={isSelected ? 'pr-1' : ''}
                          style={{ cursor: 'pointer' }}
                        >
                          {tag.name}
                        </Button>
                      );
                    })}
                  </Group>
                  {tagGroups.indexOf(group) < tagGroups.length - 1 && <Divider mt="md" />}
                </Box>
              ))
            )}
          </Stack>
        )}

        {/* 已失效标签展示 */}
        {invalidTags.length > 0 && (
          <Box mt="md">
            <Divider mb="sm" />
            <Text size="sm" c="dimmed" mb="xs">
              {t('tag.invalid_tags_label')}
            </Text>
            <Group gap="xs">
              {invalidTags.map((tag) => {
                const isSelected = selectedTagIds.has(tag.id);
                return (
                  <Button
                    key={tag.id}
                    variant={isSelected ? 'filled' : 'light'}
                    color="red"
                    size="xs"
                    onClick={() => handleTagToggle(tag.id)}
                    rightSection={
                      isSelected ? (
                        <ActionIcon
                          size="xs"
                          variant="transparent"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleTagToggle(tag.id);
                          }}
                          className="text-white hover:bg-white/20"
                        >
                          ×
                        </ActionIcon>
                      ) : null
                    }
                    className={isSelected ? 'pr-1' : ''}
                    style={{ cursor: 'pointer' }}
                  >
                    {tag.name} {t('tag.tag_deleted')}
                  </Button>
                );
              })}
            </Group>
            {invalidTags.some((tag) => selectedTagIds.has(tag.id)) && (
              <Text size="xs" c="red" mt="xs">
                {t('tag.must_remove_invalid_tags')}
              </Text>
            )}
          </Box>
        )}

        <Group justify="space-between" mt="xl">
          <Button
            variant="outline"
            color="violet"
            onClick={handleRegenerate}
            loading={regenerating}
            disabled={loadingTagGroups || saving}
          >
            {t('tag.regenerate_tags')}
          </Button>

          <Group>
            <Button variant="outline" onClick={onClose} disabled={saving || regenerating}>
              {t('tag.cancel')}
            </Button>
            <Button onClick={handleSave} loading={saving} disabled={loadingTagGroups || regenerating}>
              {t('tag.save')}
            </Button>
          </Group>
        </Group>
      </Stack>
    </Modal>
  );
};
